import { showCustomToast } from 'common/components/CustomToast/utils';
import { useMutation } from '@apollo/client';
import { IconButton, Stack, TooltipHost } from '@fluentui/react';
import { ConfirmDialog } from 'common/components/UrgencySelector/ConfirmDialog';
import { useCommonStyles } from 'common/styles';
import { InvoiceSigningUrgentUpdateInput } from 'common/types/globalTypes';
import React, { useState } from 'react';

import { InvoiceDetails } from 'common/components/Modules/TransactionEdit/graphql/__generated__/InvoiceDetails';
import {
  InvoiceSigningUrgentUpdate,
  InvoiceSigningUrgentUpdateVariables,
} from './__generated__/InvoiceSigningUrgentUpdate';
import { useStyles } from './index.styles';

import INVOICE_SIGNING_URGENCY_UPDATE from './InvoiceSigningUrgentUpdate.graphql';
interface UrgencyFolderProps {
  invoiceDetailsData?: InvoiceDetails;
}

export const UrgencyFolder: React.FC<UrgencyFolderProps> = ({
  invoiceDetailsData,
}) => {
  const styles = useStyles();
  const commonStyles = useCommonStyles();

  const [isVisible, setIsVisible] = useState(false);
  const isUrgent = !Boolean(invoiceDetailsData?.invoice?._urgencyLevel);
  const urgencyLevelToolTip = isUrgent ? 'Remove Urgency' : 'Elevate to urgent';
  const iconName = isUrgent ? 'FabricFolderFill' : 'FabricFolder';
  const iconStyle = isUrgent ? commonStyles.urgentBox : styles.standardBox;
  const isChecked = !isUrgent;
  const [invoiceSigningUrgencyUpdate] = useMutation<
    InvoiceSigningUrgentUpdate,
    InvoiceSigningUrgentUpdateVariables
  >(INVOICE_SIGNING_URGENCY_UPDATE, { errorPolicy: 'all' });

  const onSubmit = async (requestComment: string | undefined | null) => {
    const updatedUrgencyLevel =
      invoiceDetailsData?.invoice?._urgencyLevel === 0 ? 1 : 0;

    const { errors } = await invoiceSigningUrgencyUpdate({
      variables: {
        input: {
          entityId: invoiceDetailsData?.invoice?.id!,
          rowTimestamp: invoiceDetailsData?.invoice?._rowTimestamp,
          urgencyLevel: updatedUrgencyLevel,
          explanationComment: requestComment,
        } as InvoiceSigningUrgentUpdateInput,
      },
      update(cache, { data }) {
        if (!!invoiceDetailsData?.invoice?.id) {
          cache.modify({
            id: cache.identify({ ...invoiceDetailsData.invoice }),
            fields: {
              _urgencyLevel: () => {
                return data?.invoiceSigningUrgentUpdate?._newUrgentLevel;
              },
            },
          });
        }
        setIsVisible(false);
      },
    });
    if (errors?.length)
      showCustomToast({
        content: errors[0].message,
        appearance: 'error',
      });
    else {
      showCustomToast({
        content: 'Urgency status updated successfully.',
        appearance: 'success',
      });
    }
  };

  return (
    <Stack>
      <TooltipHost content={urgencyLevelToolTip}>
        <IconButton
          iconProps={{
            iconName: iconName,
          }}
          className={iconStyle}
          onClick={() => setIsVisible(true)}
          styles={{
            rootHovered: { color: 'redDark' },
            rootPressed: { color: 'redDark' },
          }}
        />
      </TooltipHost>
      <ConfirmDialog
        hidden={!isVisible}
        minWidth={500}
        checked={isChecked}
        onConfirm={(requestComment: string | null | undefined) =>
          onSubmit(requestComment)
        }
        onDismiss={() => setIsVisible(false)}
        onCancel={() => setIsVisible(false)}
      />
    </Stack>
  );
};
